/**
 * GL Account Context for managing GL account state across the application
 * Provides centralized GL account management with proper error handling
 */
import React, { createContext, useContext, useEffect, useCallback, ReactNode, useState, useRef } from 'react';
import { GLAccount } from '../types/glAccount';
import { useAsyncList } from '../hooks/useAsyncState';
import glAccountService, { GLAccountCreateData, GLAccountUpdateData } from '../services/glAccountService';
import { useOrganization } from './OrganizationContext';

interface GLAccountContextType {
  // State
  accounts: GLAccount[];
  loading: boolean;
  error: string | null;
  lastSyncTimestamp: Date | null;
  
  // Actions
  loadAccounts: () => Promise<GLAccount[]>;
  createAccount: (data: GLAccountCreateData) => Promise<GLAccount>;
  updateAccount: (id: string, data: GLAccountUpdateData) => Promise<GLAccount>;
  deleteAccount: (id: string) => Promise<void>;
  deleteAccounts: (ids: string[]) => Promise<void>;
  createBulkAccounts: (data: GLAccountCreateData[]) => Promise<any>;
  searchAccounts: (term: string) => Promise<GLAccount[]>;
  getAccountByNumber: (number: string) => GLAccount | null;
  checkNumberExists: (number: string, excludeId?: string) => Promise<boolean>;
  refreshAccounts: () => Promise<void>;
  
  // Utility functions
  reset: () => void;
}

const GLAccountContext = createContext<GLAccountContextType | undefined>(undefined);

interface GLAccountProviderProps {
  children: ReactNode;
}

export const GLAccountProvider: React.FC<GLAccountProviderProps> = ({ children }) => {
  const { organization } = useOrganization();
  const previousOrgIdRef = useRef<string | null>(null);

  // Stable async function for GL accounts
  const getGLAccounts = useCallback(
    () => glAccountService.getAll(),
    []
  );

  const {
    state: { data: accounts, loading, error },
    fetchItems: fetchAccountsInternal,
    reset,
    setData,
    addItem,
    updateItem,
    removeItem,
    removeItems
  } = useAsyncList<GLAccount>(getGLAccounts, []);

  // Track last sync timestamp
  const [lastSyncTimestamp, setLastSyncTimestamp] = useState<Date | null>(
    glAccountService.getLastSyncTimestamp()
  );

  // Wrapper to return the accounts array
  const loadAccounts = useCallback(async (): Promise<GLAccount[]> => {
    const result = await fetchAccountsInternal();
    setLastSyncTimestamp(glAccountService.getLastSyncTimestamp());
    return result;
  }, [fetchAccountsInternal]);

  // Load accounts on mount - only once
  useEffect(() => {
    let mounted = true;
    
    const loadInitialAccounts = async () => {
      try {
        await fetchAccountsInternal();
        if (mounted) {
          setLastSyncTimestamp(glAccountService.getLastSyncTimestamp());
        }
      } catch (error) {
        if (mounted) {
          console.error('Failed to load GL accounts on mount:', error);
        }
      }
    };

    loadInitialAccounts();

    return () => {
      mounted = false;
    };
  }, [fetchAccountsInternal]); // Include fetchAccountsInternal dependency

  // Background sync - validate cache periodically
  useEffect(() => {
    let mounted = true;
    
    const backgroundSync = async () => {
      try {
        const validation = await glAccountService.validateCache();
        if (!validation.isValid && mounted) {
          console.warn('Background sync detected cache issues:', validation.issues);
          
          // Auto-refresh if we detect phantom accounts or major inconsistencies
          const hasPhantomAccounts = validation.issues.some(issue => 
            issue.includes('phantom accounts') || issue.includes('count mismatch')
          );
          
          if (hasPhantomAccounts) {
            console.log('Auto-refreshing due to phantom accounts...');
            const refreshedAccounts = await glAccountService.forceRefresh();
            if (mounted) {
              setData(refreshedAccounts);
              setLastSyncTimestamp(glAccountService.getLastSyncTimestamp());
            }
          }
        }
      } catch (error) {
        // Silently log background sync errors to avoid spamming console
        if (mounted) {
          console.debug('Background sync failed:', error);
        }
      }
    };

    // Run background sync every 20 minutes
    const interval = setInterval(backgroundSync, 1200000);
    
    // Run initial background sync after 20 seconds
    const timeout = setTimeout(backgroundSync, 20000);

    return () => {
      mounted = false;
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [setData]);

  // Detect organization changes and reload data
  useEffect(() => {
    const currentOrgId = organization?.id || null;
    const previousOrgId = previousOrgIdRef.current;

    // If organization changed (and it's not the initial load)
    if (previousOrgId !== null && previousOrgId !== currentOrgId) {
      console.log('[GLAccountContext] Organization changed, reloading GL accounts');
      
      // Reset state and reload data
      reset();
      
      // Load fresh data for the new organization
      if (currentOrgId) {
        fetchAccountsInternal().then(() => {
          setLastSyncTimestamp(glAccountService.getLastSyncTimestamp());
        }).catch(error => {
          console.error('[GLAccountContext] Failed to reload GL accounts after org switch:', error);
        });
      }
    }

    // Update the ref for next comparison
    previousOrgIdRef.current = currentOrgId;
  }, [organization?.id, reset, fetchAccountsInternal]);

  // Create new account
  const createAccount = async (data: GLAccountCreateData): Promise<GLAccount> => {
    try {
      const newAccount = await glAccountService.create(data);
      addItem(newAccount);
      return newAccount;
    } catch (error) {
      console.error('Failed to create GL account:', error);
      throw error;
    }
  };

  // Update existing account
  const updateAccount = async (id: string, data: GLAccountUpdateData): Promise<GLAccount> => {
    try {
      const updatedAccount = await glAccountService.update(id, data);
      updateItem(updatedAccount, (account) => account.id);
      return updatedAccount;
    } catch (error) {
      console.error('Failed to update GL account:', error);
      throw error;
    }
  };

  // Delete single account
  const deleteAccount = async (id: string): Promise<void> => {
    try {
      await glAccountService.delete(id);
      removeItem(id, (account) => account.id);
    } catch (error: any) {
      console.error('Failed to delete GL account:', error);
      
      // If it's a stale data error, refresh the account list
      if (error?.code === 'STALE_DATA') {
        console.log('Refreshing account list due to stale data...');
        const refreshedAccounts = await glAccountService.getAll();
        setData(refreshedAccounts);
        setLastSyncTimestamp(glAccountService.getLastSyncTimestamp());
      }
      
      throw error;
    }
  };

  // Delete multiple accounts
  const deleteAccounts = async (ids: string[]): Promise<void> => {
    try {
      await glAccountService.deleteBulk(ids);
      removeItems(ids, (account) => account.id);
    } catch (error) {
      console.error('Failed to delete GL accounts:', error);
      throw error;
    }
  };

  // Create multiple accounts
  const createBulkAccounts = async (data: GLAccountCreateData[]): Promise<any> => {
    try {
      const result = await glAccountService.createBulk(data);
      
      // Add successfully created accounts to state
      if (result.successful && result.successful.length > 0) {
        const currentAccounts = accounts || [];
        setData([...currentAccounts, ...result.successful]);
      }
      
      return result;
    } catch (error) {
      console.error('Failed to create bulk GL accounts:', error);
      throw error;
    }
  };

  // Search accounts
  const searchAccounts = async (term: string): Promise<GLAccount[]> => {
    try {
      return await glAccountService.search(term);
    } catch (error) {
      console.error('Failed to search GL accounts:', error);
      throw error;
    }
  };

  // Get account by number (from current state)
  const getAccountByNumber = (number: string): GLAccount | null => {
    return accounts?.find(account => account.number === number) || null;
  };

  // Check if number exists
  const checkNumberExists = async (number: string, excludeId?: string): Promise<boolean> => {
    try {
      return await glAccountService.numberExists(number, excludeId);
    } catch (error) {
      console.error('Failed to check if GL account number exists:', error);
      throw error;
    }
  };

  // Refresh accounts from server
  const refreshAccounts = async (): Promise<void> => {
    try {
      const refreshedAccounts = await glAccountService.refresh();
      setData(refreshedAccounts);
      setLastSyncTimestamp(glAccountService.getLastSyncTimestamp());
    } catch (error) {
      console.error('Failed to refresh GL accounts:', error);
      throw error;
    }
  };

  const contextValue: GLAccountContextType = {
    // State
    accounts: accounts || [],
    loading,
    error,
    lastSyncTimestamp,
    
    // Actions
    loadAccounts,
    createAccount,
    updateAccount,
    deleteAccount,
    deleteAccounts,
    createBulkAccounts,
    searchAccounts,
    getAccountByNumber,
    checkNumberExists,
    refreshAccounts,
    
    // Utility
    reset
  };

  return (
    <GLAccountContext.Provider value={contextValue}>
      {children}
    </GLAccountContext.Provider>
  );
};

/**
 * Hook to use GL Account context
 * Throws error if used outside of GLAccountProvider
 */
export const useGLAccount = (): GLAccountContextType => {
  const context = useContext(GLAccountContext);
  if (context === undefined) {
    throw new Error('useGLAccount must be used within a GLAccountProvider');
  }
  return context;
};

/**
 * Hook for GL account operations with optimistic updates and error handling
 */
export const useGLAccountOperations = () => {
  const context = useGLAccount();
  
  const createWithValidation = async (data: GLAccountCreateData) => {
    // Check if number already exists
    const exists = await context.checkNumberExists(data.number);
    if (exists) {
      throw new Error(`GL account with number ${data.number} already exists`);
    }
    
    return context.createAccount(data);
  };
  
  const updateWithValidation = async (id: string, data: GLAccountUpdateData) => {
    // If updating number, check if it already exists
    if (data.name && typeof data.name === 'string') {
      const currentAccount = context.accounts.find(acc => acc.id === id);
      if (currentAccount && data.name !== currentAccount.name) {
        // Name is being changed, but we don't need to validate uniqueness for names
      }
    }
    
    return context.updateAccount(id, data);
  };
  
  return {
    ...context,
    createAccount: createWithValidation,
    updateAccount: updateWithValidation
  };
};

export default GLAccountContext;